import React, { useState, useEffect } from "react";
import { Select, Spin, message } from "antd";
import { CheckOutlined, MoreOutlined } from "@ant-design/icons";
import api from "../services/api";

const { Option } = Select;

// ── Helper FireDAC ─────────────────────────────────────────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const tables = data.FDBS.Manager.TableList;
      if (tables?.length > 0 && tables[0].RowList)
        return tables[0].RowList.map((r) => r.Original);
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

// ── Helpers de formatação (exibição) ──────────────────────────────────
const fmtNum = (v) =>
  v > 0
    ? Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0,00";

const fmtInt = (v) =>
  v > 0 ? Number(v).toLocaleString("pt-BR") : "0";

// Formata um custo POR MILHEIRO para exibição (ex.: 0,17 ou 15,26).
// O car_vunitario JÁ está por milheiro, então NÃO multiplicamos por 1000.
const fmtMilheiro = (porMilheiro) =>
  Number(porMilheiro).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  const compMatch = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compMatch) return `${compMatch[3]}/${compMatch[2]}/${compMatch[1]}`;
  if (s.match(/^\d{2}\/\d{2}\/\d{4}/)) return s.slice(0, 10);
  return s;
};

// ── Helpers de formatação para o backend Delphi ───────────────────────
// SaldoMilhas → StrToFloat: inteiro puro "350000", decimal com vírgula "350000,50"
const toDelphiFloat = (value) => {
  const n = Number(value);
  if (isNaN(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

// ValorClube → StrToCurr: custo unitário POR MILHEIRO em 2 casas (ex.: "0,16").
// É o PMP já calculado, IGUAL ao valor verde mostrado na tela.
const toDelphiUnitario = (pmpPorMilheiro) => {
  const n = Number(pmpPorMilheiro);
  if (isNaN(n)) return "0,00";
  const arred = Math.round(n * 100) / 100;        // 2 casas = igual ao verde
  return arred.toFixed(2).replace(".", ",");
};

// Trava contra dado legado gravado ×1000. car_vunitario deve ser o custo POR
// MILHEIRO (ex.: 0,18 / 15,00 / 44,90). Nenhum programa custa > R$100/milheiro,
// então valores acima disso são tratados como escala antiga (×1000) e divididos.
const normalizaVunitarioMilheiro = (v) => {
  const n = Number(v) || 0;
  return n > 100 ? n / 1000 : n;
};

// ── CÁLCULO DO PMP (mesma conta de Compras / EntradaManual) ────────────
// TUDO em termos de MILHEIRO. car_vunitario é o custo POR MILHEIRO.
//   custo total existente = (saldo / 1000) * vunitario_por_milheiro
const blendPMP = (saldoAtual, vunitarioRaw, pontos, custoNovoTotal) => {
  const vunitAtualMilheiro = normalizaVunitarioMilheiro(vunitarioRaw);        // custo POR MILHEIRO (com trava)
  const saldo              = Number(saldoAtual) || 0;
  const custoAtualTotal    = (saldo / 1000) * vunitAtualMilheiro;            // ex.: 83,000 * 14,90 = 1.236,70
  const saldoFinal         = saldo + (Number(pontos) || 0);                  // ex.: 84.000
  const custoFinalTotal    = custoAtualTotal + (Number(custoNovoTotal) || 0); // ex.: 1.236,70 + 44,90
  const unitAtual = vunitAtualMilheiro;                                 // custo/milheiro existente
  const unitNovo  = (Number(pontos)||0) > 0 ? (Number(custoNovoTotal)||0) / ((Number(pontos)||0) / 1000) : vunitAtualMilheiro;
  let pmpPorMilheiro = saldoFinal > 0 ? custoFinalTotal / (saldoFinal / 1000) : 0; // ex.: 15,26
  const _lo = Math.min(unitAtual, unitNovo), _hi = Math.max(unitAtual, unitNovo);
  if (pmpPorMilheiro < _lo) pmpPorMilheiro = _lo;
  if (pmpPorMilheiro > _hi) pmpPorMilheiro = _hi;
  return { saldoAtual: saldo, vunitAtualMilheiro, custoAtualTotal, saldoFinal, custoFinalTotal, pmpPorMilheiro };
};

export default function Compensar({ clienteId: clienteIdProp }) {
  const [loading, setLoading]     = useState(false);
  const [registros, setRegistros] = useState([]);
  const [titular, setTitular]     = useState(null);
  const [clienteId, setClienteId] = useState(null);
  const [cartaoMap, setCartaoMap] = useState({}); // car_id -> { saldo, vunitario } (para o preview)

  // Filtros
  const [filtroTitular, setFiltroTitular] = useState(null);

  // ── Resolve clienteId ─────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
      if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    }
    if (id) setClienteId(id);
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  useEffect(() => {
    if (!clienteId) return;
    carregarDados();
  }, [clienteId]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const res  = await api.get("/ServerPrincipal/PesquisaCompensa", { params: { Cliente: clienteId } });
      const rows = extractRows(res.data);
      setRegistros(rows);
      await carregarCartoes(rows);
    } catch (e) {
      message.error("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  // Carrega saldo + custo atual de cada cartão envolvido (apenas para o preview da coluna)
  const carregarCartoes = async (rows) => {
    const cliIds = [...new Set(rows.map((r) => r.cli_id ?? r.CLI_ID).filter(Boolean))];
    const map = {};
    await Promise.all(
      cliIds.map(async (cid) => {
        try {
          const r = await api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: cid } });
          extractRows(r.data).forEach((c) => {
            const id = c.car_id ?? c.CAR_ID;
            if (id != null)
              map[id] = {
                saldo: Number(c.car_saldo_milhas ?? c.CAR_SALDO_MILHAS ?? 0),
                vunitario: Number(c.car_vunitario ?? c.CAR_VUNITARIO ?? 0),
              };
          });
        } catch (e) {}
      })
    );
    setCartaoMap(map);
  };

  // PMP para a coluna de preview (usa os dados já carregados)
  const previewPMP = (reg) => {
    const carId = reg.car_id ?? reg.CAR_ID;
    const card = cartaoMap[carId];
    if (!card) return null;
    const pontos    = Number(reg.clu_pontos       ?? reg.CLU_PONTOS       ?? 0);
    const custoNovo = Number(reg.clu_vmensalidade ?? reg.CLU_VMENSALIDADE ?? 0);
    return blendPMP(card.saldo, card.vunitario, pontos, custoNovo);
  };

  // ── Compensar ─────────────────────────────────────────────────────
  // Ao clicar: busca o cartão correto, lê o custo atual, calcula o PMP com a
  // entrada que está chegando e manda SÓ o valor final no ValorClube.
  // O backend (original) soma os pontos ao saldo e grava o ValorClube no
  // car_vunitario — por isso aqui mandamos o PMP já pronto (por milheiro).
  const handleCompensar = async (reg) => {
    const carId       = reg.car_id          ?? reg.CAR_ID          ?? 0;
    const cliId       = reg.cli_id          ?? reg.CLI_ID          ?? clienteId;
    const clubeId     = reg.clu_id          ?? reg.CLU_ID          ?? 0;
    const pontos      = Number(reg.clu_pontos       ?? reg.CLU_PONTOS       ?? 0); // entrada nova
    const mensalidade = Number(reg.clu_vmensalidade ?? reg.CLU_VMENSALIDADE ?? 0); // custo total da entrada
    const prog        = reg.car_nome_programa ?? reg.CAR_NOME_PROGRAMA ?? "";

    setLoading(true);
    try {
      // 1) busca o cartão correto e seu custo atual (dado fresco)
      const carRes = await api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: cliId } });
      const card = extractRows(carRes.data).find((c) => (c.car_id ?? c.CAR_ID) === carId);

      const saldoAtual   = Number(card?.car_saldo_milhas ?? card?.CAR_SALDO_MILHAS ?? 0);
      const vunitarioRaw = Number(card?.car_vunitario    ?? card?.CAR_VUNITARIO    ?? 0); // por milheiro

      // 2) calcula o PMP (mesma conta das outras telas)
      const { pmpPorMilheiro } = blendPMP(saldoAtual, vunitarioRaw, pontos, mensalidade);

      // 3) monta o payload mandando o PMP já calculado (por milheiro)
      const payload = {
        IDCliente:    cliId,
        IDCartao:     carId,
        IDClube:      clubeId,
        SaldoMilhas:  toDelphiFloat(pontos),             // pontos novos (servidor soma ao saldo)
        NomePrograma: prog,
        ValorClube:   toDelphiUnitario(pmpPorMilheiro),  // PMP por milheiro -> car_vunitario
      };

      console.log("Payload InserirCompensa:", JSON.stringify(payload, null, 2));

      await api.post("/ServerPrincipal/InserirCompensa", payload);

      message.success(
        `Compensação realizada! Novo custo médio: R$ ${fmtMilheiro(pmpPorMilheiro)}/milheiro`
      );

      carregarDados();
    } catch (e) {
      message.error(e.response?.data || "Erro ao compensar.");
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers para nome do titular e programa de cada registro ─────
  const getNomeTitular = (reg) =>
    reg.cli_nome ?? reg.CLI_NOME ?? titular?.cli_nome ?? "";

  const nomePrograma = (reg) =>
    reg.car_nome_programa ?? reg.CAR_NOME_PROGRAMA ?? "";

  // Titulares únicos para o filtro
  const titularesUnicos = [...new Set(
    registros.map((r) => getNomeTitular(r)).filter(Boolean)
  )];

  const registrosFiltrados = registros.filter((r) => {
    if (!filtroTitular) return true;
    return getNomeTitular(r) === filtroTitular;
  });

  const isVencido = (reg) => {
    const v = reg.clu_vencimento ?? reg.CLU_VENCIMENTO ?? "";
    if (!v) return false;
    try { return new Date(v) < new Date(); } catch { return false; }
  };

  // ── Estilos ───────────────────────────────────────────────────────
  const th = {
    padding: "10px 16px", fontSize: 12, fontWeight: 600,
    color: "#888", textAlign: "left",
    borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap",
  };
  const tdStyle = (vencido) => ({
    padding: "14px 16px", fontSize: 13,
    color: vencido ? "#e8003d" : "#333",
    borderBottom: "1px solid #f7f7f7", whiteSpace: "nowrap",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", padding: "24px 16px" }}>
      <Spin spinning={loading}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>

          {/* Título */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <CheckOutlined style={{ fontSize: 16, color: "#444" }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: "#222" }}>Compensação</span>
          </div>

          {/* Filtros */}
          <div style={{
            background: "#1e2a38", borderRadius: 8, padding: "16px 20px",
            marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: "#aab", marginBottom: 6 }}>Filtrar por Titular</div>
              <Select
                size="middle" style={{ width: "100%" }}
                placeholder="Todos os titulares"
                allowClear
                onChange={(v) => setFiltroTitular(v ?? null)}
                value={filtroTitular}
              >
                {titularesUnicos.map((nome) => (
                  <Option key={nome} value={nome}>{nome}</Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: "#aab", marginBottom: 6 }}>Filtrar por Tipo de Operação</div>
              <Select
                size="middle" style={{ width: "100%" }}
                placeholder="Todas operações"
                allowClear
                disabled
              />
            </div>
          </div>

          {/* Tabela */}
          <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={th}>Titular da Conta</th>
                  <th style={th}>Tipo da Operação</th>
                  <th style={th}>Data</th>
                  <th style={{ ...th, display: "flex", alignItems: "center", gap: 4 }}>
                    Compensação <span style={{ fontSize: 10, color: "#bbb" }}>▼</span>
                  </th>
                  <th style={{ ...th, textAlign: "right" }}>Quantidade</th>
                  <th style={{ ...th, textAlign: "right" }}>Valor</th>
                  <th style={{ ...th, textAlign: "right" }}>Custo Médio (após)</th>
                  <th style={{ ...th, textAlign: "center" }}>Compensar</th>
                  <th style={{ ...th, width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: "40px 16px", textAlign: "center", color: "#bbb", fontSize: 13 }}>
                      {loading ? "Carregando..." : "Nenhum registro encontrado"}
                    </td>
                  </tr>
                ) : (
                  registrosFiltrados.map((reg, i) => {
                    const vencido     = isVencido(reg);
                    const prog        = nomePrograma(reg);
                    const nomeCliente = getNomeTitular(reg);
                    const titular_    = `${nomeCliente}${prog ? " - " + prog : ""}`;
                    const tipo        = prog ? `Clube ${prog}` : "Clube";
                    const data        = fmtDate(reg.clu_vencimento ?? reg.CLU_VENCIMENTO ?? "");
                    const comp        = fmtDate(reg.clu_vencimento ?? reg.CLU_VENCIMENTO ?? "");
                    const qtde        = Number(reg.clu_pontos       ?? reg.CLU_PONTOS       ?? 0);
                    const valor       = Number(reg.clu_vmensalidade ?? reg.CLU_VMENSALIDADE ?? 0);
                    const pmp         = previewPMP(reg);

                    return (
                      <tr key={reg.clu_id ?? i}
                        style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={tdStyle(vencido)}>{titular_}</td>
                        <td style={tdStyle(vencido)}>{tipo}</td>
                        <td style={tdStyle(vencido)}>{data}</td>
                        <td style={tdStyle(vencido)}>{comp}</td>
                        <td style={{ ...tdStyle(vencido), textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {fmtInt(qtde)}
                        </td>
                        <td style={{ ...tdStyle(vencido), textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {fmtNum(valor)}
                        </td>
                        <td style={{ ...tdStyle(false), textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#237804", fontWeight: 600 }}>
                          {pmp ? `R$ ${fmtMilheiro(pmp.pmpPorMilheiro)}` : "—"}
                        </td>
                        <td style={{ ...tdStyle(false), textAlign: "center" }}>
                          <button
                            onClick={() => handleCompensar(reg)}
                            style={{
                              padding: "4px 14px",
                              background: "#fff",
                              border: "1px solid #f5a800",
                              borderRadius: 4,
                              color: "#f5a800",
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            COMPENSAR
                          </button>
                        </td>
                        <td style={{ ...tdStyle(false), textAlign: "center", padding: "14px 8px" }}>
                          <MoreOutlined style={{ fontSize: 16, color: "#bbb", cursor: "pointer" }} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

        </div>
      </Spin>
    </div>
  );
}